import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  TextInput,
  Card,
  SegmentedButtons,
  Chip,
  useTheme,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import {
  BrewingMethod,
  RecipeDifficulty,
  GrindSize,
} from '@coffeeplexity/shared';

const NumberInput = ({
  value,
  onChange,
  onBlur,
  ...props
}: Omit<React.ComponentProps<typeof TextInput>, 'value' | 'onChangeText'> & {
  value?: number | null;
  onChange: (value?: number) => void;
}) => {
  const [internalValue, setInternalValue] = useState(value?.toString() ?? '');
  const [isFocused, setIsFocused] = useState(false);

  // Only sync external value when not focused
  useEffect(() => {
    if (!isFocused) {
      if (value === undefined || value === null) {
        setInternalValue('');
      } else {
        setInternalValue(value.toString());
      }
    }
  }, [value, isFocused]);

  const handleChangeText = (text: string) => {
    const cleanText = text.replace(/[^0-9]/g, '');
    setInternalValue(cleanText);

    if (cleanText === '') {
      onChange(undefined);
    } else {
      const val = parseInt(cleanText, 10);
      if (!isNaN(val)) onChange(val);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    onBlur?.(e);

    // Sync with external value on blur
    if (value !== undefined && value !== null) {
      setInternalValue(value.toString());
    } else {
      setInternalValue('');
    }
  };

  return (
    <TextInput
      {...props}
      value={internalValue}
      onChangeText={handleChangeText}
      onFocus={handleFocus}
      onBlur={handleBlur}
      keyboardType='numeric'
    />
  );
};

export function RecipeBasicInfo() {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const brewingMethodOptions = [
    { value: BrewingMethod.ESPRESSO, label: 'Espresso' },
    { value: BrewingMethod.POUR_OVER, label: 'Pour Over' },
    { value: BrewingMethod.FRENCH_PRESS, label: 'French Press' },
    { value: BrewingMethod.AEROPRESS, label: 'AeroPress' },
    { value: BrewingMethod.COLD_BREW, label: 'Cold Brew' },
  ];

  const difficultyOptions = [
    { value: RecipeDifficulty.BEGINNER, label: 'Beginner' },
    { value: RecipeDifficulty.INTERMEDIATE, label: 'Intermediate' },
    { value: RecipeDifficulty.ADVANCED, label: 'Advanced' },
    { value: RecipeDifficulty.EXPERT, label: 'Expert' },
  ];

  const grindSizeOptions = [
    { value: GrindSize.EXTRA_COARSE, label: 'Extra Coarse' },
    { value: GrindSize.COARSE, label: 'Coarse' },
    { value: GrindSize.MEDIUM_COARSE, label: 'Medium Coarse' },
    { value: GrindSize.MEDIUM, label: 'Medium' },
    { value: GrindSize.MEDIUM_FINE, label: 'Medium Fine' },
    { value: GrindSize.FINE, label: 'Fine' },
    { value: GrindSize.EXTRA_FINE, label: 'Extra Fine' },
  ];

  return (
    <View style={styles.container}>
      {/* Basic Information */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineSmall' style={styles.sectionTitle}>
            Basic Information
          </Text>

          {/* Title */}
          <Controller
            control={control}
            name='title'
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                label='Recipe Title *'
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors['title']}
                style={styles.input}
                placeholder='e.g., Perfect Morning Pour Over'
              />
            )}
          />
          {errors['title'] && (
            <Text
              variant='bodySmall'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              {errors['title'].message as string}
            </Text>
          )}

          {/* Description */}
          <Controller
            control={control}
            name='description'
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                label='Description *'
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors['description']}
                multiline
                numberOfLines={4}
                style={styles.input}
                placeholder='Describe your recipe and what makes it special...'
              />
            )}
          />
          {errors['description'] && (
            <Text
              variant='bodySmall'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              {errors['description'].message as string}
            </Text>
          )}

          {/* Brewing Method */}
          <Text variant='titleMedium' style={styles.fieldLabel}>
            Brewing Method *
          </Text>
          <Controller
            control={control}
            name='brewing_method'
            render={({ field: { onChange, value } }) => (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.chipRow}
                contentContainerStyle={styles.chipRowContent}
              >
                {brewingMethodOptions.map(opt => (
                  <View key={opt.value} style={styles.chipWrapper}>
                    <Chip
                      selected={value === opt.value}
                      onPress={() => onChange(opt.value)}
                      style={styles.chip}
                      compact={false}
                      textStyle={styles.chipLabel}
                    >
                      {opt.label}
                    </Chip>
                  </View>
                ))}
              </ScrollView>
            )}
          />

          {/* Difficulty */}
          <Text variant='titleMedium' style={styles.fieldLabel}>
            Difficulty Level *
          </Text>
          <Controller
            control={control}
            name='difficulty'
            render={({ field: { onChange, value } }) => (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.chipRow}
                contentContainerStyle={styles.chipRowContent}
              >
                {difficultyOptions.map(opt => (
                  <View key={opt.value} style={styles.chipWrapper}>
                    <Chip
                      selected={value === opt.value}
                      onPress={() => onChange(opt.value)}
                      style={styles.chip}
                      compact={false}
                      textStyle={styles.chipLabel}
                    >
                      {opt.label}
                    </Chip>
                  </View>
                ))}
              </ScrollView>
            )}
          />

          {/* Servings and Time */}
          <View style={styles.row}>
            <View style={styles.halfWidth}>
              <Controller
                control={control}
                name='servings'
                render={({ field: { onChange, onBlur, value } }) => (
                  <NumberInput
                    label='Servings *'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors['servings']}
                    style={styles.input}
                    placeholder='1'
                  />
                )}
              />
            </View>
            <View style={styles.halfWidth}>
              <Controller
                control={control}
                name='prep_time_minutes'
                render={({ field: { onChange, onBlur, value } }) => (
                  <NumberInput
                    label='Prep Time (min) *'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors['prep_time_minutes']}
                    style={styles.input}
                    placeholder='5'
                  />
                )}
              />
            </View>
          </View>

          <Controller
            control={control}
            name='total_time_minutes'
            render={({ field: { onChange, onBlur, value } }) => (
              <NumberInput
                label='Total Time (min) *'
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors['total_time_minutes']}
                style={styles.input}
                placeholder='10'
              />
            )}
          />
        </Card.Content>
      </Card>

      {/* Brewing Parameters */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineSmall' style={styles.sectionTitle}>
            Brewing Parameters
          </Text>

          {/* Grind Size */}
          <Text variant='titleMedium' style={styles.fieldLabel}>
            Grind Size *
          </Text>
          <Controller
            control={control}
            name='brewing_parameters.grind_size'
            render={({ field: { onChange, value } }) => (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.chipRow}
                contentContainerStyle={styles.chipRowContent}
              >
                {grindSizeOptions.slice(0, 4).map(opt => (
                  <View key={opt.value} style={styles.chipWrapper}>
                    <Chip
                      selected={value === opt.value}
                      onPress={() => onChange(opt.value)}
                      style={styles.chip}
                      compact={false}
                      textStyle={styles.chipLabel}
                    >
                      {opt.label}
                    </Chip>
                  </View>
                ))}
              </ScrollView>
            )}
          />

          {/* Water Temperature */}
          <Controller
            control={control}
            name='brewing_parameters.water_temperature_celsius'
            render={({ field: { onChange, onBlur, value } }) => (
              <NumberInput
                label='Water Temperature (°C) *'
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors['brewing_parameters']?.water_temperature_celsius}
                style={styles.input}
                placeholder='85'
              />
            )}
          />

          {/* Coffee to Water Ratio */}
          <Controller
            control={control}
            name='brewing_parameters.coffee_to_water_ratio'
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                label='Coffee to Water Ratio *'
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                style={styles.input}
                placeholder='1:15'
              />
            )}
          />

          {/* Brew Time */}
          <Controller
            control={control}
            name='brewing_parameters.total_brew_time_seconds'
            render={({ field: { onChange, onBlur, value } }) => (
              <NumberInput
                label='Total Brew Time (seconds) *'
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors['brewing_parameters']?.total_brew_time_seconds}
                style={styles.input}
                placeholder='240'
              />
            )}
          />

          {/* Optional Parameters */}
          <View style={styles.row}>
            <View style={styles.halfWidth}>
              <Controller
                control={control}
                name='brewing_parameters.bloom_time_seconds'
                render={({ field: { onChange, onBlur, value } }) => (
                  <NumberInput
                    label='Bloom Time (sec)'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    style={styles.input}
                    placeholder='30'
                  />
                )}
              />
            </View>
            <View style={styles.halfWidth}>
              <Controller
                control={control}
                name='brewing_parameters.pressure_bars'
                render={({ field: { onChange, onBlur, value } }) => (
                  <NumberInput
                    label='Pressure (bars)'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    style={styles.input}
                    placeholder='9'
                  />
                )}
              />
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Basic Info
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Use descriptive titles that highlight what's unique{'\n'}• Include
            brewing method and key characteristics{'\n'}• Be specific about
            timing and measurements{'\n'}• Consider your target audience's skill
            level
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
  },
  card: {
    marginBottom: 16,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  fieldLabel: {
    marginTop: 16,
    marginBottom: 8,
  },
  input: {
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  segmentedButtons: {
    marginBottom: 16,
  },
  chipRow: {
    marginBottom: 16,
  },
  chipRowContent: {
    paddingRight: 8,
    gap: 8,
  },
  chipWrapper: {
    flexShrink: 0,
  },
  chip: {
    height: 40,
    borderRadius: 20,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  segmentedButton: {
    height: 44,
    borderRadius: 22,
    paddingHorizontal: 14,
    justifyContent: 'center',
  },
  segmentedButtonLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  errorText: {
    marginTop: 4,
    marginBottom: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
